import { useId, useState, type ReactNode } from 'react'

/**
 * Graphiques en SVG ecrits a la main.
 *
 * Aucune bibliotheque de visualisation : la politique de securite du site
 * interdit tout script tiers et l'application doit fonctionner integralement
 * hors ligne. Le SVG inline satisfait les deux sans peser un octet de plus.
 *
 * ## Regles suivies
 *
 * - marques fines, extremites arrondies a 4 px ancrees a la ligne de base ;
 * - 2 px de fond entre deux barres voisines, jamais de contour pour les separer ;
 * - grille et axes en filets pleins, jamais en pointilles ;
 * - pas d'etiquette sur chaque point : seuls les extremes sont annotes, le reste
 *   passe par l'infobulle et la vue tableau ;
 * - chaque graphique possede son equivalent en tableau, car une infobulle ne
 *   doit jamais etre le seul moyen de lire une valeur (et il n'y a pas de survol
 *   sur un ecran tactile).
 */

// ---------------------------------------------------------------------------
// Enveloppe commune : titre, graphique, bascule vers le tableau
// ---------------------------------------------------------------------------

export function Figure({
  titre,
  description,
  children,
  tableau,
}: {
  titre: string
  description?: string
  children: ReactNode
  tableau: ReactNode
}) {
  const [vueTableau, setVueTableau] = useState(false)
  return (
    <figure className="figure">
      <figcaption className="ligne ligne--espacee ligne--haut">
        <div className="grandir">
          <h3>{titre}</h3>
          {description && (
            <p className="tres-doux" style={{ marginTop: 2 }}>
              {description}
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn btn--fantome btn--petit"
          aria-pressed={vueTableau}
          onClick={() => setVueTableau((v) => !v)}
        >
          {vueTableau ? 'Graphique' : 'Chiffres'}
        </button>
      </figcaption>
      {vueTableau ? <div className="defilable">{tableau}</div> : children}
    </figure>
  )
}

/** Barre verticale a extremite haute arrondie, ancree a la ligne de base. */
function cheminBarre(x: number, y: number, l: number, h: number): string {
  if (h <= 0) return ''
  const r = Math.min(4, l / 2, h)
  return `M${x},${y + h}L${x},${y + r}Q${x},${y} ${x + r},${y}L${x + l - r},${y}Q${x + l},${y} ${x + l},${y + r}L${x + l},${y + h}Z`
}

// ---------------------------------------------------------------------------
// Barres temporelles
// ---------------------------------------------------------------------------

export interface PointBarre {
  cle: string
  libelle: string
  valeur: number
  /** Couleur CSS ; par defaut la serie principale. */
  couleur?: string
  info?: string
}

export function BarresVerticales({
  points,
  hauteur = 150,
  uniteY = '',
  labelY,
}: {
  points: PointBarre[]
  hauteur?: number
  uniteY?: string
  labelY?: string
}) {
  const id = useId()
  if (points.length === 0) return <p className="doux">Aucune donnée sur la période.</p>

  const max = Math.max(1, ...points.map((p) => p.valeur))
  // Graduations entieres et peu nombreuses : la grille doit rester discrete.
  const pas = max <= 4 ? 1 : max <= 10 ? 2 : Math.ceil(max / 5)
  const graduations: number[] = []
  for (let v = 0; v <= max; v += pas) graduations.push(v)

  const margeG = 26
  const margeB = 18
  const largeurBarre = 10
  const ecart = 2
  const largeur = margeG + points.length * (largeurBarre + ecart)
  const hPlot = hauteur - margeB

  const y = (v: number) => hPlot - (v / max) * (hPlot - 6)
  // Etiquetage selectif : jamais un nombre sur chaque barre.
  const pasEtiquette = Math.ceil(points.length / 8)

  return (
    <div className="defilable">
      <svg
        className="viz"
        width={largeur}
        height={hauteur}
        viewBox={`0 0 ${largeur} ${hauteur}`}
        role="img"
        aria-label={labelY ?? 'Graphique en barres'}
        style={{ minWidth: '100%' }}
      >
        {graduations.map((g) => (
          <g key={g}>
            <line
              x1={margeG}
              x2={largeur}
              y1={y(g)}
              y2={y(g)}
              stroke="var(--viz-grille)"
              strokeWidth="1"
            />
            <text x={margeG - 6} y={y(g) + 3.5} textAnchor="end" className="viz__tick">
              {g}
              {uniteY}
            </text>
          </g>
        ))}

        {points.map((p, i) => {
          const x = margeG + i * (largeurBarre + ecart)
          const h = hPlot - y(p.valeur)
          return (
            <g key={`${id}-${p.cle}`}>
              {/* Zone de survol elargie : une barre de 10 px est trop fine a viser. */}
              <rect x={x - ecart} y={0} width={largeurBarre + ecart * 2} height={hPlot} fill="transparent">
                <title>{p.info ?? `${p.libelle} : ${p.valeur}${uniteY}`}</title>
              </rect>
              {p.valeur > 0 && (
                <path d={cheminBarre(x, y(p.valeur), largeurBarre, h)} fill={p.couleur ?? 'var(--viz-serie)'}>
                  <title>{p.info ?? `${p.libelle} : ${p.valeur}${uniteY}`}</title>
                </path>
              )}
              {i % pasEtiquette === 0 && (
                <text x={x + largeurBarre / 2} y={hauteur - 5} textAnchor="middle" className="viz__tick">
                  {p.libelle}
                </text>
              )}
            </g>
          )
        })}

        <line x1={margeG} x2={largeur} y1={hPlot} y2={hPlot} stroke="var(--viz-axe)" strokeWidth="1" />
      </svg>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Barres horizontales (classements)
// ---------------------------------------------------------------------------

export function BarresHorizontales({ points }: { points: PointBarre[] }) {
  if (points.length === 0) return <p className="doux">Aucune donnée sur la période.</p>
  const max = Math.max(1, ...points.map((p) => p.valeur))

  return (
    <ul className="pile pile--serree" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {points.map((p) => (
        <li key={p.cle}>
          <div className="ligne ligne--espacee" style={{ marginBottom: 3 }}>
            <span className="doux grandir" style={{ minWidth: 0 }}>
              {p.libelle}
            </span>
            <span className="gras mono">{p.valeur}</span>
          </div>
          <div
            className="viz__piste"
            role="img"
            aria-label={`${p.libelle} : ${p.valeur}`}
            title={`${p.libelle} : ${p.valeur}`}
          >
            <div
              className="viz__remplissage"
              style={{
                width: `${Math.max(2, (p.valeur / max) * 100)}%`,
                background: p.couleur ?? 'var(--viz-serie)',
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  )
}

// ---------------------------------------------------------------------------
// Nuage de points par facette
// ---------------------------------------------------------------------------

export interface PointNuage {
  t: number
  y: number
  info: string
}

export function NuageFacette({
  points,
  tMin,
  tMax,
  yMax = 10,
  hauteur = 74,
}: {
  points: PointNuage[]
  tMin: number
  tMax: number
  yMax?: number
  hauteur?: number
}) {
  const largeur = 320
  const margeG = 20
  const margeB = 4
  const hPlot = hauteur - margeB
  const etendue = Math.max(1, tMax - tMin)

  const x = (t: number) => margeG + ((t - tMin) / etendue) * (largeur - margeG - 8)
  const y = (v: number) => hPlot - (Math.max(0, Math.min(yMax, v)) / yMax) * (hPlot - 10)

  return (
    <svg
      className="viz"
      viewBox={`0 0 ${largeur} ${hauteur}`}
      width="100%"
      height={hauteur}
      preserveAspectRatio="none"
      role="img"
      aria-label={`${points.length} évènement(s)`}
    >
      {[0, yMax / 2, yMax].map((g) => (
        <g key={g}>
          <line x1={margeG} x2={largeur} y1={y(g)} y2={y(g)} stroke="var(--viz-grille)" strokeWidth="1" />
          <text x={margeG - 4} y={y(g) + 3} textAnchor="end" className="viz__tick">
            {g}
          </text>
        </g>
      ))}
      {points.map((p, i) => (
        // Anneau de 2 px a la couleur du fond : separe les points superposes
        // sans les cerner d'un contour.
        <circle
          key={i}
          cx={x(p.t)}
          cy={y(p.y)}
          r={4.5}
          fill="var(--viz-serie)"
          stroke="var(--viz-surface)"
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        >
          <title>{p.info}</title>
        </circle>
      ))}
    </svg>
  )
}
